// Register structure management page — admin-only.
// Manages the risk register hierarchy. Live editing — no draft/activate cycle.
// Mirrors the CategoryPage pattern but uses register API endpoints.

import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi, ApiRequestError } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import {
  registersApi,
  buildRegisterTree,
  type RegisterTreeNode,
} from '@/lib/api/registers'
import { ownershipRolesApi } from '@/lib/api/ownershipRoles'
import { queryKeys } from '@/lib/api/queryKeys'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { useToast } from '@/lib/hooks/use-toast'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
} from '@/components/ui/context-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  Trash2,
  ChevronRight,
  ChevronDown,
  Plus,
  Pencil,
  Loader2,
} from 'lucide-react'

export function RegisterStructurePage() {
  const api = useApi()
  const { slug } = useTenant()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const regApi = registersApi(api)

  // --- State for dialogs ---
  const [addTarget, setAddTarget] = useState<RegisterTreeNode | null>(null)
  const [editTarget, setEditTarget] = useState<RegisterTreeNode | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<RegisterTreeNode | null>(null)

  // --- Query: load tree ---
  const treeQuery = useQuery({
    queryKey: queryKeys.registers.tree(slug),
    queryFn: () => regApi.tree(),
  })

  const tree = treeQuery.data ? buildRegisterTree(treeQuery.data) : []
  const nodeCount = treeQuery.data?.length ?? 0

  const invalidateTree = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.registers.all(slug),
    })
  }, [queryClient, slug])

  // --- Mutations ---

  const createMutation = useMutation({
    mutationFn: (params: { parentId: string; name: string; description: string; ownerRoleId: string }) =>
      regApi.create({
        parent_id: params.parentId,
        name: params.name,
        description: params.description || undefined,
        owner_role_id: params.ownerRoleId,
      }),
    onSuccess: (created) => {
      invalidateTree()
      setAddTarget(null)
      toast({ title: `Created "${created.name}"` })
    },
    onError: (err) => {
      toast({
        title: 'Failed to create register',
        description: err instanceof ApiRequestError ? err.message : 'An error occurred',
        variant: 'destructive',
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: (params: { id: string; name: string; description: string }) =>
      regApi.update(params.id, {
        name: params.name,
        description: params.description || undefined,
      }),
    onSuccess: (updated) => {
      invalidateTree()
      setEditTarget(null)
      toast({ title: `Updated "${updated.name}"` })
    },
    onError: (err) => {
      toast({
        title: 'Failed to update register',
        description: err instanceof ApiRequestError ? err.message : 'An error occurred',
        variant: 'destructive',
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => regApi.delete(id),
    onSuccess: () => {
      invalidateTree()
      const name = deleteTarget?.name
      setDeleteTarget(null)
      toast({ title: `Deleted "${name}"` })
    },
    onError: (err) => {
      setDeleteTarget(null)
      toast({
        title: 'Failed to delete register',
        description: err instanceof ApiRequestError ? err.message : 'An error occurred',
        variant: 'destructive',
      })
    },
  })

  // --- Handlers ---

  const handleAddChild = useCallback((node: RegisterTreeNode) => {
    setAddTarget(node)
  }, [])

  const handleAddConfirm = useCallback(
    (name: string, description: string, ownerRoleId: string) => {
      if (!addTarget) return
      createMutation.mutate({
        parentId: addTarget.id,
        name,
        description,
        ownerRoleId,
      })
    },
    [addTarget, createMutation],
  )

  const handleEdit = useCallback((node: RegisterTreeNode) => {
    setEditTarget(node)
  }, [])

  const handleEditConfirm = useCallback(
    (name: string, description: string) => {
      if (!editTarget) return
      updateMutation.mutate({ id: editTarget.id, name, description })
    },
    [editTarget, updateMutation],
  )

  const handleDelete = useCallback((node: RegisterTreeNode) => {
    setDeleteTarget(node)
  }, [])

  // --- Loading/error ---

  if (treeQuery.isLoading) {
    return <TableSkeleton />
  }

  if (treeQuery.error) {
    return (
      <ErrorState
        message="Failed to load register structure"
        technicalDetail={
          treeQuery.error instanceof ApiRequestError
            ? treeQuery.error.message
            : treeQuery.error.message
        }
        onRetry={() => treeQuery.refetch()}
      />
    )
  }

  return (
    <div className="max-w-4xl space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold">Register Structure</h1>
        <p className="text-muted-foreground">
          Manage the risk register hierarchy. Registers organise risks and control who has access to them.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {nodeCount} {nodeCount === 1 ? 'register' : 'registers'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <RegisterTreeEditor
            roots={tree}
            onAddChild={handleAddChild}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </CardContent>
      </Card>

      {/* Add Register Dialog */}
      <AddRegisterDialog
        open={addTarget !== null}
        parentName={addTarget?.name ?? ''}
        onConfirm={handleAddConfirm}
        onCancel={() => setAddTarget(null)}
        isPending={createMutation.isPending}
      />

      {/* Edit Register Dialog */}
      <EditRegisterDialog
        open={editTarget !== null}
        currentName={editTarget?.name ?? ''}
        currentDescription={editTarget?.description ?? ''}
        onConfirm={handleEditConfirm}
        onCancel={() => setEditTarget(null)}
        isPending={updateMutation.isPending}
      />

      {/* Delete Confirmation */}
      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &ldquo;{deleteTarget?.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              This register will be permanently deleted. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// --- Register Tree Editor ---

interface RegisterTreeEditorProps {
  roots: RegisterTreeNode[]
  onAddChild: (parentNode: RegisterTreeNode) => void
  onEdit: (node: RegisterTreeNode) => void
  onDelete: (node: RegisterTreeNode) => void
}

function RegisterTreeEditor({ roots, onAddChild, onEdit, onDelete }: RegisterTreeEditorProps) {
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const initial = new Set<string>()
    function expand(nodes: RegisterTreeNode[]) {
      for (const node of nodes) {
        if (node.children.length > 0 && node.depth <= 1) {
          initial.add(node.id)
          expand(node.children)
        }
      }
    }
    expand(roots)
    return initial
  })

  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const handleAddChild = useCallback(
    (node: RegisterTreeNode) => {
      setExpanded((prev) => {
        const next = new Set(prev)
        next.add(node.id)
        return next
      })
      onAddChild(node)
    },
    [onAddChild],
  )

  if (roots.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        No registers found. The root register should be created during tenant setup.
      </div>
    )
  }

  return (
    <div className="space-y-0.5">
      {roots.map((root) => (
        <RegisterNodeRow
          key={root.id}
          node={root}
          expanded={expanded}
          onToggle={toggle}
          onAddChild={handleAddChild}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  )
}

// --- Register Node Row ---

interface RegisterNodeRowProps {
  node: RegisterTreeNode
  expanded: Set<string>
  onToggle: (id: string) => void
  onAddChild: (node: RegisterTreeNode) => void
  onEdit: (node: RegisterTreeNode) => void
  onDelete: (node: RegisterTreeNode) => void
}

function RegisterNodeRow({
  node,
  expanded,
  onToggle,
  onAddChild,
  onEdit,
  onDelete,
}: RegisterNodeRowProps) {
  const hasChildren = node.has_children || node.children.length > 0
  const isExpanded = expanded.has(node.id)
  const isRoot = node.depth === 0
  const indent = node.depth * 24

  const canDelete = !isRoot && node.risk_count === 0 && !node.has_children

  return (
    <div>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div
            className={`group flex items-center gap-1.5 rounded-sm py-1.5 pr-2 hover:bg-accent/50 ${
              node.depth === 0 ? 'font-semibold' : ''
            } ${node.depth === 1 ? 'text-sm font-medium' : ''} ${
              node.depth >= 2 ? 'text-sm text-muted-foreground' : ''
            }`}
            style={{ paddingLeft: `${indent + 4}px` }}
          >
            {/* Expand/collapse chevron */}
            {hasChildren ? (
              <button
                onClick={() => onToggle(node.id)}
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-sm hover:bg-muted"
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
              </button>
            ) : (
              <span className="w-5 shrink-0" />
            )}

            {/* Path segment */}
            <span className="w-10 shrink-0 text-xs tabular-nums text-muted-foreground/60">
              {node.path.split('.').pop()}
            </span>

            {/* Name with optional description tooltip */}
            {node.description ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-default">{node.name}</span>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs">
                  <p className="text-sm">{node.description}</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <span>{node.name}</span>
            )}

            {/* Metadata — always visible, right-aligned */}
            <div className="ml-auto flex items-center gap-4">
              <span className="w-36 truncate text-right text-xs italic text-muted-foreground/70">
                {node.owner_display_name ?? 'unassigned'}
              </span>
              <span className="w-8 text-right text-xs tabular-nums text-muted-foreground">
                {node.risk_count}
              </span>
            </div>
          </div>
        </ContextMenuTrigger>

        <ContextMenuContent>
          <ContextMenuItem onClick={() => onAddChild(node)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Child
          </ContextMenuItem>
          {!isRoot && (
            <>
              <ContextMenuItem onClick={() => onEdit(node)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </ContextMenuItem>
              <ContextMenuSeparator />
              <ContextMenuItem
                onClick={() => onDelete(node)}
                className="text-destructive focus:text-destructive"
                disabled={!canDelete}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
                {node.has_children && ' (has children)'}
                {node.risk_count > 0 && ' (has risks)'}
              </ContextMenuItem>
            </>
          )}
        </ContextMenuContent>
      </ContextMenu>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div>
          {node.children.map((child) => (
            <RegisterNodeRow
              key={child.id}
              node={child}
              expanded={expanded}
              onToggle={onToggle}
              onAddChild={onAddChild}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// --- Add Register Dialog ---

interface AddRegisterDialogProps {
  open: boolean
  parentName: string
  onConfirm: (name: string, description: string, ownerRoleId: string) => void
  onCancel: () => void
  isPending?: boolean
}

function AddRegisterDialog({ open, parentName, onConfirm, onCancel, isPending }: AddRegisterDialogProps) {
  const api = useApi()
  const { slug } = useTenant()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [ownerRoleId, setOwnerRoleId] = useState('')

  const rolesQuery = useQuery({
    queryKey: ['tenant', slug, 'ownership-roles'],
    queryFn: () => ownershipRolesApi(api).list(),
    enabled: open,
  })

  const roles = rolesQuery.data ?? []
  const canSubmit = name.trim() && ownerRoleId && !isPending

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (canSubmit) {
      onConfirm(name.trim(), description.trim(), ownerRoleId)
    }
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onCancel()
      resetForm()
    }
  }

  function resetForm() {
    setName('')
    setDescription('')
    setOwnerRoleId('')
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add register under &ldquo;{parentName}&rdquo;</DialogTitle>
          <DialogDescription>
            This register will be created immediately as a child of the selected register.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="reg-name">Name</Label>
              <Input
                id="reg-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Technology Risk"
                maxLength={200}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reg-description">Description (optional)</Label>
              <Input
                id="reg-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this register"
                maxLength={1000}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reg-owner-role">Owner Role</Label>
              {rolesQuery.isLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading roles...
                </div>
              ) : (
                <select
                  id="reg-owner-role"
                  value={ownerRoleId}
                  onChange={(e) => setOwnerRoleId(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">Select an ownership role</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.label}
                      {role.owner_display_name ? ` (${role.owner_display_name})` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Add Register'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// --- Edit Register Dialog ---

interface EditRegisterDialogProps {
  open: boolean
  currentName: string
  currentDescription: string
  onConfirm: (name: string, description: string) => void
  onCancel: () => void
  isPending?: boolean
}

function EditRegisterDialog({
  open,
  currentName,
  currentDescription,
  onConfirm,
  onCancel,
  isPending,
}: EditRegisterDialogProps) {
  const [name, setName] = useState(currentName)
  const [description, setDescription] = useState(currentDescription)

  // Sync state when dialog opens with new target
  const [prevName, setPrevName] = useState(currentName)
  if (currentName !== prevName) {
    setName(currentName)
    setDescription(currentDescription)
    setPrevName(currentName)
  }

  const canSubmit = name.trim() && !isPending

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (canSubmit) {
      onConfirm(name.trim(), description.trim())
    }
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) onCancel()
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Register</DialogTitle>
          <DialogDescription>
            Update the register name and description.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="edit-reg-name">Name</Label>
              <Input
                id="edit-reg-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={200}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-reg-description">Description (optional)</Label>
              <Input
                id="edit-reg-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={1000}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
